import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ThemeContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Font options
const fontOptions = {
  families: [
    { name: 'Inter', value: "'Inter', sans-serif" },
    { name: 'Plus Jakarta Sans', value: "'Plus Jakarta Sans', sans-serif" },
    { name: 'Poppins', value: "'Poppins', sans-serif" },
    { name: 'Nunito', value: "'Nunito', sans-serif" },
    { name: 'Open Sans', value: "'Open Sans', sans-serif" },
    { name: 'Roboto', value: "'Roboto', sans-serif" },
    { name: 'Lato', value: "'Lato', sans-serif" },
    { name: 'Playfair Display', value: "'Playfair Display', serif" },
    { name: 'Quicksand', value: "'Quicksand', sans-serif" },
    { name: 'Comic Neue', value: "'Comic Neue', cursive" }
  ],
  sizes: [
    { name: 'Small', value: '14px' },
    { name: 'Normal', value: '16px' },
    { name: 'Large', value: '18px' },
    { name: 'Extra Large', value: '20px' }
  ],
  styles: ['Normal', 'Italic'],
  weights: [
    { name: 'Light', value: '300' },
    { name: 'Normal', value: '400' },
    { name: 'Medium', value: '500' },
    { name: 'Bold', value: '700' }
  ]
};

// Preset background colors
const backgroundColors = [
  { name: 'Slate Dark', value: '#0f172a', isDark: true },
  { name: 'Purple Night', value: '#1e1b4b', isDark: true },
  { name: 'Deep Blue', value: '#0c1e3c', isDark: true },
  { name: 'Dark Teal', value: '#0d2e2e', isDark: true },
  { name: 'Midnight', value: '#111827', isDark: true },
  { name: 'White', value: '#ffffff', isDark: false },
  { name: 'Light Gray', value: '#f8fafc', isDark: false },
  { name: 'Warm White', value: '#fffbf0', isDark: false },
  { name: 'Cool Blue', value: '#f0f9ff', isDark: false },
  { name: 'Soft Pink', value: '#fdf2f8', isDark: false }
];

// Preset button colors
const buttonColors = [
  { name: 'Cyan-Violet', primary: 'from-cyan-500 to-violet-500', secondary: 'from-slate-500 to-slate-600' },
  { name: 'Blue-Purple', primary: 'from-blue-500 to-purple-600', secondary: 'from-gray-500 to-gray-600' },
  { name: 'Green-Teal', primary: 'from-emerald-500 to-teal-500', secondary: 'from-slate-500 to-slate-600' },
  { name: 'Orange-Pink', primary: 'from-orange-500 to-pink-500', secondary: 'from-slate-500 to-slate-600' },
  { name: 'Rose-Red', primary: 'from-rose-500 to-red-500', secondary: 'from-gray-500 to-gray-600' },
  { name: 'Indigo', primary: 'from-indigo-500 to-indigo-600', secondary: 'from-slate-500 to-slate-600' }
];

// Shadow intensity options
const shadowIntensities = {
  low: '0 1px 3px rgba(0, 0, 0, 0.12)',
  medium: '0 4px 6px rgba(0, 0, 0, 0.1)',
  high: '0 10px 25px rgba(0, 0, 0, 0.15)'
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Dark/Light mode
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('vocalab_dark_mode');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Background color
  const [backgroundColor, setBackgroundColorState] = useState(() => {
    return localStorage.getItem('vocalab_bg_color') || '#0f172a';
  });

  // Font settings
  const [fontSettings, setFontSettings] = useState(() => {
    const saved = localStorage.getItem('vocalab_font_settings');
    return saved ? JSON.parse(saved) : {
      family: 'Inter',
      size: '16px',
      color: '#ffffff',
      style: 'Normal',
      weight: '400'
    };
  });

  // Button colors
  const [buttonColorScheme, setButtonColorScheme] = useState(() => {
    return localStorage.getItem('vocalab_button_colors') || 'Cyan-Violet';
  });

  // Card customization settings
  const [cardSettings, setCardSettings] = useState(() => {
    const saved = localStorage.getItem('vocalab_card_settings');
    return saved ? JSON.parse(saved) : {
      bgColor: 'rgba(15, 23, 42, 0.95)',
      gradient: false,
      borderColor: 'rgba(255, 255, 255, 0.1)',
      textColor: '#ffffff',
      shadowIntensity: 'medium'
    };
  });

  // Load theme from database on mount (if user is logged in)
  useEffect(() => {
    const loadThemeFromDatabase = async () => {
      const token = localStorage.getItem('vocalab_token');
      if (!token) return;

      try {
        const response = await axios.get(`${API_URL}/api/theme-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          const dbTheme = response.data;
          
          // Update states from database
          if (dbTheme.background_color) setBackgroundColorState(dbTheme.background_color);
          if (dbTheme.button_color) setButtonColorScheme(dbTheme.button_color);
          if (dbTheme.card_bg_color || dbTheme.card_gradient !== undefined) {
            setCardSettings({
              bgColor: dbTheme.card_bg_color || cardSettings.bgColor,
              gradient: dbTheme.card_gradient || false,
              borderColor: dbTheme.card_border_color || cardSettings.borderColor,
              textColor: dbTheme.card_text_color || cardSettings.textColor,
              shadowIntensity: dbTheme.shadow_intensity || cardSettings.shadowIntensity
            });
          }
        }
      } catch (error) {
        console.log('Theme settings not found in database, using local storage');
      }
    };

    // Load immediately if token exists
    loadThemeFromDatabase();
    
    // Listen for storage changes (when user logs in from another tab or after login)
    const handleStorageChange = (e) => {
      if (e.key === 'vocalab_token' && e.newValue) {
        loadThemeFromDatabase();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically after mount in case login happened
    const checkInterval = setInterval(() => {
      const token = localStorage.getItem('vocalab_token');
      if (token) {
        loadThemeFromDatabase();
        clearInterval(checkInterval);
      }
    }, 1000);
    
    // Clear interval after 10 seconds
    setTimeout(() => clearInterval(checkInterval), 10000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkInterval);
    };
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('vocalab_dark_mode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('vocalab_bg_color', backgroundColor);
  }, [backgroundColor]);

  useEffect(() => {
    localStorage.setItem('vocalab_font_settings', JSON.stringify(fontSettings));
  }, [fontSettings]);

  useEffect(() => {
    localStorage.setItem('vocalab_button_colors', buttonColorScheme);
  }, [buttonColorScheme]);

  useEffect(() => {
    localStorage.setItem('vocalab_card_settings', JSON.stringify(cardSettings));
  }, [cardSettings]);

  // Apply theme to document with CSS variables
  useEffect(() => {
    const root = document.documentElement;
    
    // Dark/Light mode
    root.classList.toggle('dark', isDarkMode);
    
    // Font settings
    root.style.setProperty('--font-body', `'${fontSettings.family}', sans-serif`);
    root.style.setProperty('--font-size', fontSettings.size);
    
    // Background color
    root.style.setProperty('--bg-color', backgroundColor);
    
    // Card settings - Apply CSS variables with light/dark mode support
    if (isDarkMode) {
      root.style.setProperty('--card-bg', cardSettings.bgColor);
      root.style.setProperty('--card-border', cardSettings.borderColor);
      root.style.setProperty('--card-text', cardSettings.textColor);
    } else {
      // Light mode card colors
      root.style.setProperty('--card-bg', '#ffffff');
      root.style.setProperty('--card-border', 'rgba(226, 232, 240, 1)');
      root.style.setProperty('--card-text', '#0f172a');
    }
    root.style.setProperty('--card-shadow', shadowIntensities[cardSettings.shadowIntensity]);
    root.style.setProperty('--card-gradient', cardSettings.gradient ? '1' : '0');
    
    // Button color
    const buttonScheme = buttonColors.find(b => b.name === buttonColorScheme) || buttonColors[0];
    root.style.setProperty('--button-primary', buttonScheme.primary);
    root.style.setProperty('--button-secondary', buttonScheme.secondary);
  }, [isDarkMode, fontSettings, backgroundColor, buttonColorScheme, cardSettings]);

  // Save theme to database
  const saveThemeToDatabase = async (updates) => {
    const token = localStorage.getItem('vocalab_token');
    if (!token) return;

    try {
      await axios.put(`${API_URL}/api/theme-settings`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to save theme to database:', error);
    }
  };

  // Update card setting and save to DB
  const updateCardSetting = (key, value) => {
    const newSettings = { ...cardSettings, [key]: value };
    setCardSettings(newSettings);
    
    // Map to database field names
    const dbFieldMap = {
      bgColor: 'card_bg_color',
      gradient: 'card_gradient',
      borderColor: 'card_border_color',
      textColor: 'card_text_color',
      shadowIntensity: 'shadow_intensity'
    };
    
    saveThemeToDatabase({ [dbFieldMap[key]]: value });
  };

  // Get current button colors
  const getCurrentButtonColors = () => {
    return buttonColors.find(b => b.name === buttonColorScheme) || buttonColors[0];
  };

  // Build theme object
  const theme = {
    name: 'Premium Gradient',
    isDark: isDarkMode,
    fonts: {
      heading: fontSettings.family,
      body: fontSettings.family
    },
    colors: {
      primary: getCurrentButtonColors().primary,
      secondary: getCurrentButtonColors().secondary,
      accent: '#8b5cf6',
      background: isDarkMode 
        ? `bg-gradient-to-br from-[${backgroundColor}] via-purple-900/30 to-[${backgroundColor}]`
        : `bg-[${backgroundColor}]`,
      backgroundColor: backgroundColor,
      surface: isDarkMode ? 'bg-white/10 backdrop-blur-xl' : 'bg-white',
      surfaceLight: 'bg-white',
      text: isDarkMode ? 'text-white' : 'text-slate-900',
      textMuted: isDarkMode ? 'text-slate-300' : 'text-slate-600',
      border: isDarkMode ? 'border-white/20' : 'border-slate-200',
      cardBg: isDarkMode 
        ? 'bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20'
        : 'bg-white shadow-sm border border-slate-200',
      buttonPrimary: `bg-gradient-to-r ${getCurrentButtonColors().primary} hover:opacity-90`,
      buttonSecondary: `bg-gradient-to-r ${getCurrentButtonColors().secondary} hover:opacity-90`,
      sidebar: isDarkMode 
        ? 'bg-gradient-to-b from-slate-900/95 to-purple-900/95 backdrop-blur-xl'
        : 'bg-white border-r border-slate-200',
      header: isDarkMode
        ? 'bg-gradient-to-r from-slate-900/90 to-purple-900/90 backdrop-blur-xl'
        : 'bg-white/95 backdrop-blur-sm border-b border-slate-200'
    },
    card: cardSettings
  };

  const value = {
    // Theme object
    theme,
    isDark: isDarkMode,
    
    // Dark/Light mode
    isDarkMode,
    setIsDarkMode: (value) => {
      setIsDarkMode(value);
      saveThemeToDatabase({ background_color: backgroundColor });
    },
    toggleDarkMode: () => {
      setIsDarkMode(prev => !prev);
    },
    
    // Background color
    backgroundColor,
    setBackgroundColor: (value) => {
      setBackgroundColorState(value);
      saveThemeToDatabase({ background_color: value });
    },
    backgroundColors,
    
    // Font settings
    fontSettings,
    setFontSettings,
    updateFontSetting: (key, value) => setFontSettings(prev => ({ ...prev, [key]: value })),
    fontOptions,
    
    // Button colors
    buttonColorScheme,
    setButtonColorScheme: (value) => {
      setButtonColorScheme(value);
      saveThemeToDatabase({ button_color: value, button_style: 'gradient' });
    },
    buttonColors,
    getCurrentButtonColors,
    
    // Card settings
    cardSettings,
    setCardSettings,
    updateCardSetting,
    shadowIntensities,
    
    // Legacy compatibility
    currentTheme: 'premium',
    themes: { premium: theme },
    colorScheme: buttonColorScheme,
    setColorScheme: setButtonColorScheme,
    colorSchemes: { premium: buttonColors.map(b => b.name) },
    customFonts: { heading: fontSettings.family, body: fontSettings.family },
    setCustomFonts: () => {}
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
